<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { formatPrice } from '@/utils/helpers'
import Distributions from '@/config/Distributions'
import { useCartStore } from '@/stores/useCartStore'

const cartStore = useCartStore()

const {t} = useI18n()
</script>

<template>
  <div class="cart-summary"
       data-test-id="cart-summary"
       v-if="cartStore.basketInfo && cartStore.basketInfo.costs">
    <div class="cart-summary__line" data-test-id="cart-summary-sub-total">
      <div class="cart-summary__text">
        <span class="bold">
          {{ t('cart.sub-total') }}
        </span>
      </div>

      <div class="cart-summary__text">
        <span class="bold">
          {{ formatPrice(cartStore.basketInfo.costs.subtotal) }}
        </span>
      </div>
    </div>

    <div v-if="cartStore.basketInfo.costs.total_order_discount > 0"
         class="cart-summary__line"
         data-test-id="cart-summary-discount"
    >
      <div class="cart-summary__text">
        {{ t('receipt.discount') }}
      </div>
      <div class="cart-summary__text">
        <span class="gradient bold">
          {{ formatPrice(cartStore.basketInfo.costs.total_order_discount) }}
        </span>
      </div>
    </div>

    <div v-if="cartStore.basketInfo.costs.coupon_has_discount"
         class="cart-summary__line"
    >
      <div class="cart-summary__text">
        {{ t('cart.coupon') }}
      </div>
      <div class="cart-summary__text">
        <span class="gradient bold">
          {{ formatPrice(cartStore.basketInfo.costs.coupon_discount) }}
        </span>
      </div>
    </div>

    <div v-if="cartStore.basketInfo.costs.deducted_by_giftcard > 0"
         class="cart-summary__line"
         data-test-id="cart-summary-giftcard"
    >
      <div class="cart-summary__text">
        {{ t('cart.giftcard') }}
      </div>
      <div class="cart-summary__text">
        {{ formatPrice(cartStore.basketInfo.costs.deducted_by_giftcard) }}
      </div>
    </div>

    <div v-if="cartStore.distribution === Distributions.Delivery"
         class="cart-summary__line"
         data-test-id="cart-summary-delivery-costs">
      <div class="cart-summary__text">
        {{ t('terms.delivery') }}
      </div>
      <div class="cart-summary__text">
        <template v-if="cartStore.basketInfo.costs.delivery_costs_after_discount === 0">
          <span class="gradient bold">
            {{ t('terms.free') }}
          </span>
        </template>
        <template v-else>
          {{ formatPrice(cartStore.basketInfo.costs.delivery_costs_after_discount) }}
        </template>
      </div>
    </div>

    <div class="cart-summary__line" data-test-id="cart-summary-service-fee">
      <div class="cart-summary__text">
        {{ t('cart.service-fee') }}
      </div>
      <div class="cart-summary__text">
        {{ formatPrice(cartStore.basketInfo.costs.service_fee) }}
      </div>
    </div>

    <div class="cart-summary__line" v-if="cartStore.basketInfo.costs.total_sup_surcharge > 0">
      <div class="cart-summary__text">
        {{ t('cart.sup-fee') }}
      </div>
      <div class="cart-summary__text">
        {{ formatPrice(cartStore.basketInfo.costs.total_sup_surcharge) }}
      </div>
    </div>
    <div class="cart-summary__line" v-if="cartStore.basketInfo.costs.total_deposit_surcharge > 0">
      <div class="cart-summary__text">
        {{ t('cart.deposit-fee') }}
      </div>
      <div class="cart-summary__text">
        {{ formatPrice(cartStore.basketInfo.costs.total_deposit_surcharge) }}
      </div>
    </div>
    <div class="cart-summary__line"
         data-test-id="cart-summary-tip"
         v-if="cartStore.basketInfo.costs.restaurant_tip > 0">
      <div class="cart-summary__text">
        {{ t('cart.tip') }}
      </div>
      <div class="cart-summary__text">
        {{ formatPrice(cartStore.basketInfo.costs.restaurant_tip) }}
      </div>
    </div>

    <div class="cart-summary__line" data-test-id="cart-summary-total">
      <div class="cart-summary__text bold">
        {{ t('cart.total') }}
      </div>
      <div class="cart-summary__text bold">
        {{ formatPrice(cartStore.basketInfo.costs.total_payable) }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/styling.scss";
@import "@/assets/css/mixins/breakpoints-up.scss";

.cart-summary {
  display:        flex;
  flex-direction: column;
  gap:            0.25rem;

  &__line {
    display:         flex;
    justify-content: space-between;
  }

  &__text {
    .gradient {
      @include gradient-text;
    }
  }
}
</style>
